/**
 * Extract book covers from various file formats.
 * Format dispatch goes through the format registry — never hardcode file types.
 */
import JSZip from 'jszip';
import { getFormatConfig } from './formats/registry';
import { PDFJS_WASM_URL } from './pdf/pdfAssets';

/**
 * Extract cover image from an EPUB file.
 *
 * Returns null when the EPUB genuinely has no cover (no manifest item
 * matches any of the 4 cover-lookup strategies — this is a legitimate
 * absence for EPUBs without a cover image). **Throws** on corruption
 * (invalid ZIP, malformed OPF, etc.) — the outer `extractBookCover`
 * wrapper below is the single absence layer and turns throws into null.
 *
 * @param {ArrayBuffer} epubData - The EPUB file data
 * @returns {Promise<string|null>} - Base64 data URL of the cover image or null
 */
export async function extractEpubCover(epubData) {
  const zip = await JSZip.loadAsync(epubData);

  // Find container.xml to get the OPF path
  const containerXml = await zip.file('META-INF/container.xml')?.async('text');
  if (!containerXml) return null;

  const parser = new DOMParser();
  const containerDoc = parser.parseFromString(containerXml, 'application/xml');
  const rootfileEl = containerDoc.querySelector('rootfile');
  const opfPath = rootfileEl?.getAttribute('full-path');
  if (!opfPath) return null;

  // Parse OPF to find cover
  const opfContent = await zip.file(opfPath)?.async('text');
  if (!opfContent) return null;

  const opfDoc = parser.parseFromString(opfContent, 'application/xml');
  const basePath = opfPath.includes('/') ? opfPath.substring(0, opfPath.lastIndexOf('/') + 1) : '';

  // Try multiple strategies to find the cover
  let coverHref = null;

  // Strategy 1: Look for meta cover reference
  const coverMeta = opfDoc.querySelector('meta[name="cover"]');
  if (coverMeta) {
    const coverId = coverMeta.getAttribute('content');
    const coverItem = opfDoc.querySelector(`item[id="${coverId}"]`);
    if (coverItem) {
      coverHref = coverItem.getAttribute('href');
    }
  }

  // Strategy 2: Look for item with properties="cover-image"
  if (!coverHref) {
    const coverImageItem = opfDoc.querySelector('item[properties*="cover-image"]');
    if (coverImageItem) {
      coverHref = coverImageItem.getAttribute('href');
    }
  }

  // Strategy 3: Look for item with id containing "cover"
  if (!coverHref) {
    const items = opfDoc.querySelectorAll('item');
    for (const item of items) {
      const id = item.getAttribute('id')?.toLowerCase() || '';
      const href = item.getAttribute('href')?.toLowerCase() || '';
      const mediaType = item.getAttribute('media-type') || '';

      if (mediaType.startsWith('image/') &&
        (id.includes('cover') || href.includes('cover'))) {
        coverHref = item.getAttribute('href');
        break;
      }
    }
  }

  // Strategy 4: Look for first image in the manifest
  if (!coverHref) {
    const imageItem = opfDoc.querySelector('item[media-type^="image/"]');
    if (imageItem) {
      coverHref = imageItem.getAttribute('href');
    }
  }

  if (!coverHref) return null;

  // Resolve the cover path
  const coverPath = coverHref.startsWith('/')
    ? coverHref.substring(1)
    : basePath + coverHref;

  // Extract the cover image
  const coverFile = zip.file(coverPath);
  if (!coverFile) return null;

  const coverData = await coverFile.async('base64');

  // Determine media type
  const ext = coverPath.split('.').pop()?.toLowerCase();
  const mimeTypes = {
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',
  };
  const mimeType = mimeTypes[ext] || 'image/jpeg';

  return `data:${mimeType};base64,${coverData}`;
}

/**
 * Extract a cover image from a PDF by rendering page 1 to a canvas.
 *
 * §P3.9 Phase B follow-up F1: PDF→EPUB conversion doesn't generate a server-
 * side cover (Cloudflare Workers have no canvas, and adding browser-rendering
 * infra is overkill). Instead we extract client-side at upload time so the
 * Library card has a real cover from the first paint, regardless of where in
 * the conversion pipeline the book is.
 *
 * Returns null on any pdfjs failure (corrupt PDF, encrypted, DRM, no canvas).
 *
 * @param {ArrayBuffer} pdfData - the PDF file bytes
 * @returns {Promise<string|null>} JPEG data URL (~600px tall) or null
 */
export async function extractPdfCover(pdfData) {
  try {
    const pdfjs = await import('pdfjs-dist');
    if (!pdfjs.GlobalWorkerOptions.workerSrc) {
      const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
    }
    // Slice into a fresh buffer so we don't mutate the caller's view.
    const buffer = pdfData.slice(0);
    // NOTE: do NOT set disableFontFace / useSystemFonts: false here. Those
    // flags are correct for *text extraction* (preflightPdfPages, backend
    // pdf-extract) but break rendering — text glyphs come out blank because
    // pdfjs has no fonts to draw with. Cover extraction needs a visual
    // image, so we let pdfjs use its default font handling.
    const doc = await pdfjs.getDocument({ data: buffer, verbosity: 0, wasmUrl: PDFJS_WASM_URL }).promise;
    try {
      const page = await doc.getPage(1);
      // Aim for ~600px tall — enough for Library cards (which display ~200px),
      // small enough to not balloon IDB usage.
      const viewport = page.getViewport({ scale: 1 });
      const targetHeight = 600;
      const scale = targetHeight / viewport.height;
      const scaled = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(scaled.width);
      canvas.height = Math.ceil(scaled.height);
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      // Fill white first — most PDFs have transparent background; without
      // this, dark-themed Library shows ugly black pages.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: ctx, viewport: scaled }).promise;
      return canvas.toDataURL('image/jpeg', 0.85);
    } finally {
      await doc.destroy();
    }
  } catch (e) {
    console.warn('[extractPdfCover] failed:', e);
    return null;
  }
}

/**
 * Extract cover from a book file based on its type.
 *
 * Dispatch is fully driven by the format registry: every supported format
 * declares its own coverExtractor capability, and we look it up here. To add
 * cover extraction for a new format, add the entry to formats/registry.js —
 * this function never needs to change.
 *
 * @param {ArrayBuffer} fileData - The book file data
 * @param {string} fileType - The file type (epub, mobi, ...)
 * @returns {Promise<string|null>} - Base64 data URL of the cover or null
 */
export async function extractBookCover(fileData, fileType) {
  if (!fileType) return null;
  const config = getFormatConfig(fileType);
  if (!config?.coverExtractor) return null;
  try {
    const mod = await config.coverExtractor();
    return await mod.extract(fileData);
  } catch (e) {
    console.warn(`[extractBookCover] ${fileType} cover extraction failed:`, e);
    return null;
  }
}

export default extractBookCover;
